import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQueries } from 'react-query';
import moment from 'moment';

import Doughnut from './Doughnut';
import DateRangeFilter from 'components/ui/filters/items/DateRangeFilter';
import ObjectBlockMultiFilter from 'components/ui/filters/items/ObjectBlockMultiFilter';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useSearch from 'hooks/useSearch';
import useSetQuery from 'hooks/useSetQuery';

export default function Conversion() {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const query = useSearch();
  const setQuery = useSetQuery();
  const { search } = useLocation();

  const [hasError, setHasError] = useState(false);

  const defaults = useMemo(
    () => ({
      from: moment().subtract(30, 'days').format('YYYY-MM-DD'),
      till: moment().format('YYYY-MM-DD'),
    }),
    []
  );

  // URLda from/till bo'lmasa - default 30 kunlik oraliq qo'yiladi
  useEffect(() => {
    if (!query.get('from') && !query.get('till')) {
      setQuery('from', defaults.from);
      setQuery('till', defaults.till);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const from = query.get('from') || defaults.from;
  const till = query.get('till') || defaults.till;

  const buildUrl = (path) => {
    const params = new URLSearchParams(search);
    params.set('start', from);
    params.set('finish', till);
    return `${path}?${params.toString()}`;
  };

  const [conversion, rejected] = useQueries([
    {
      queryKey: ['/analytic/crm/conversions', search, from, till],
      queryFn: async () => {
        const res = await axiosPrivate.get(buildUrl('/analytic/crm/conversions'));
        return res.data.data;
      },
      enabled: !hasError && Boolean(from && till),
      onError: () => setHasError(true),
      retry: false,
    },
    {
      queryKey: ['/analytic/crm/rejectleads', search, from, till],
      queryFn: async () => {
        const res = await axiosPrivate.get(buildUrl('/analytic/crm/rejectleads'));
        return res.data.data;
      },
      enabled: !hasError && Boolean(from && till),
      onError: () => setHasError(true),
      retry: false,
    },
  ]);

  // Birinchi doughnut uchun ma'lumotlar
  const conversions = useMemo(
    () => [
      {
        name: t('boss.crm.conversion.rejected'),
        count: conversion?.data?.reject || 0,
      },
      {
        name: t('boss.crm.conversion.inProgress'),
        count: conversion?.data?.waiting || 0,
      },
    ],
    [conversion?.data, t]
  );

  // Bekor qilish sabablari bo'yicha
  const rejectedLeads = useMemo(
    () =>
      (rejected?.data?.data || []).filter(
        (item) => (item?.count ?? 0) > 0
      ),
    [rejected?.data]
  );

  return (
    <div className='component-wrapper boss-sale-wrapper !base-border !shadow-md'>
      <div className='flex justify-between items-center flex-wrap gap-2'>
        <div className='font-semibold text-lg'>
          {t('boss.crm.conversion.title')}
        </div>
        <div className='flex items-center gap-3 flex-wrap'>
          <div className='min-w-[300px]'>
            <ObjectBlockMultiFilter />
          </div>
          <DateRangeFilter
            labels={{
              start: t('common.filter.dateFrom'),
              end: t('common.table.till'),
            }}
          />
        </div>
      </div>

      <Doughnut
        variant='horizontal'
        title={t('boss.crm.conversion.byLead')}
        label={t('boss.crm.conversion.allLead')}
        dataList={conversions}
        isLoading={conversion?.isLoading || conversion?.isFetching}
      />

      <div className='mt-4'>
        <Doughnut
          variant='vertical'
          title={t('boss.crm.conversion.byRejectReason')}
          label={t('common.table.total')}
          isLoading={rejected?.isLoading || rejected?.isFetching}
          dataList={rejectedLeads}
        />
      </div>
    </div>
  );
}
